/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { getMockTransactions, getTransactions } from "../data/getTransactions";
import countries from "../data/json/countries.json";
import { Arc } from "../types/arc";
import { Ring } from "../types/ring";
import { Label } from "../types/label";
import * as c from "../constants";

/**
 * Renderer-agnostic transaction stream. Polls the API, replays transactions
 * second-by-second, and exposes the live arcs / rings / labels plus the running
 * count. Shared by the globe and the flat map so both render the same data.
 */
export const useTransactionStream = () => {
  const [transactionCount, setTransactionCount] = useState<number>(0);
  const [arcsData, setArcsData] = useState<Arc[]>([]);
  const [ringsData, setRingsData] = useState<Ring[]>([]);
  const [labelData, setLabelData] = useState<Label[]>([]);
  const [jsonHelper, setJsonHelper] = useState<number>(0);
  const time = useRef<number>();
  const countRef = useRef(transactionCount);

  const mockData = new URLSearchParams(window.location.search).get("md");
  const { data, refetch } = useQuery(
    "transactions",
    mockData === "true" ? getMockTransactions : getTransactions,
    {}
  );

  // Keep ref updated with the latest transactionCount
  useEffect(() => {
    countRef.current = transactionCount;
  }, [transactionCount]);

  // Estimate growth between server refetches by advancing the count
  useEffect(() => {
    const interval = setInterval(() => {
      const n = new Date();
      const perSecond = transactionCount / n.getHours() / 3600;
      setTransactionCount(countRef.current + Math.floor(perSecond * 5));
    }, 5000);
    return () => clearInterval(interval);
  }, [countRef.current]);

  const emitArc = (arc: Arc, label: Label) => {
    setArcsData((cur) => [...cur, arc]);
    setTimeout(
      () => setArcsData((cur) => cur.filter((d) => d !== arc)),
      c.ARC_TIME
    );
    // add and remove the target ring + label once the arc has landed
    setTimeout(() => {
      const targetRing = { lat: arc.endLat, lng: arc.endLng };
      setRingsData((cur) => [...cur, targetRing]);
      setLabelData((cur) => [...cur, label]);

      setTimeout(() => {
        setRingsData((cur) => cur.filter((r) => r !== targetRing));
        setLabelData((cur) => cur.filter((r) => r !== label));
      }, c.FLIGHT_TIME * c.ARC_REL_LEN);
    }, c.FLIGHT_TIME);
  };

  useEffect(() => {
    let interval: number;

    if (data) {
      try {
        time.current = 1;
        const max = data.transactions[0].secondsOffsetFromQueryTime || 0;
        setTransactionCount(data.totalSinceMidnight[0].totalSinceMidnight);
        interval = window.setInterval(function () {
          if (time.current! <= max) {
            try {
              const t = data.transactions.filter(
                (tr) => tr.secondsOffsetFromQueryTime === time.current
              );
              if (t && t.length > 0) {
                t.forEach((k) => {
                  const toISO = k.toCountryISO as keyof typeof countries;
                  const fromISO = k.fromCountryISO as keyof typeof countries;
                  const start = countries[fromISO];
                  const end = countries[toISO];
                  if (start && end) {
                    const label: Label = {
                      amount: k.sendAmount,
                      currency: k.sendCurrencyISO,
                      from: k.fromCountryISO,
                      destinationCode: k.destinationCode,
                      to: k.toCountryISO,
                      lat: end.latitude,
                      lng: end.longitude,
                      type: k.distributorCategory,
                      productType: k.productType,
                    };
                    emitArc(
                      {
                        startLat: start.latitude,
                        startLng: start.longitude,
                        endLat: end.latitude,
                        endLng: end.longitude,
                        type: k.distributorCategory.toLowerCase().replace(" ", ""),
                      },
                      label
                    );
                  }
                });
              }
              time.current = time.current! + 1;
            } catch (error) {
              console.error("data error", error);
            }
          } else {
            clearInterval(interval);
            refetch();
            setJsonHelper((v) => v + 1); // force refresh for json file
          }
        }, 1000);
      } catch (error) {
        console.error("transaction error", error);
      }
    }
    return () => {
      clearInterval(interval);
    };
  }, [data, jsonHelper]);

  return { transactionCount, arcsData, ringsData, labelData };
};
